import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';

import { TranslocoPipe } from '@jsverse/transloco';
import { ContentRatingGate } from '@core/content-ratings';
import { SettingsStore } from '@core/settings';
import { ContentRatingLock } from '@shared/components/content-rating-lock/content-rating-lock';
import { BadgeComponent } from '@shared/components/badge/badge.component';
import { BadgePosition, BadgeVariant } from '@shared/components/badge/badge.model';
import { ClockService } from '@shared/utils';
import { MiniDetailsDirective } from '@features/mini-details/components/mini-details.directive';
import { PlaybackProgressStore } from '@features/player/data-access/playback-progress.store';
import { SkinningService } from '@core/skin';
import { TranscodeImageDirective } from '@shared/directives/transcode-image.directive';
import { assetDurationSeconds } from '@shared/utils/duration.helper';

import { DisplayFormat, IAssetListItem, ListSize } from './asset-list-item.types';

@Component({
    selector: 'asset-card',
    standalone: true,
    imports: [BadgeComponent, ContentRatingLock, MiniDetailsDirective, TranscodeImageDirective, TranslocoPipe],
    templateUrl: './asset-card.html',
    styleUrl: './asset-card.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[class.selected]': 'selected()',
        '[class.portrait]': 'displayFormat() === "portrait"',
        '[class.landscape]': 'displayFormat() === "landscape"',
        '[class.square]': 'displayFormat() === "square"',
        '[class.circle]': 'displayFormat() === "circle"',
        '[class.radio]': 'displayFormat() === "radio"',
        '[class.size-xsmall]': 'size() === "xsmall"',
        '[class.size-small]': 'size() === "small"',
        '[class.size-medium]': 'size() === "medium"',
        '[class.size-large]': 'size() === "large"',
        // Grid-only: fill the cell width and take height from the format's aspect
        // ratio, instead of the fixed per-size dimensions. Carousels never set this.
        '[class.fluid]': 'fluid()',
        '[class.restricted]': 'restricted()',
    },
})
export class AssetCard {
    private readonly settings = inject(SettingsStore);
    private readonly clock = inject(ClockService);
    private readonly playbackProgress = inject(PlaybackProgressStore);
    private readonly gate = inject(ContentRatingGate);
    protected readonly skinning = inject(SkinningService);

    readonly item = input.required<IAssetListItem>();
    readonly displayFormat = input<DisplayFormat>('landscape');
    readonly size = input<ListSize>('small');
    /** Grid fluid mode: fill the cell width, height from the format's aspect ratio. */
    readonly fluid = input(false);
    readonly selected = input(false);
    readonly active = input(false);
    readonly topChart = input(false);
    readonly index = input(0);
    /**
     * Air time under programme tiles. On by default: everywhere but home's "now on TV"
     * row (search, videostore, my-content, …) a programme always prints its window, as it
     * did in legacy. Only that one row is governed by the operator setting
     * `ShowTimeonLiveCatalogue` — the home page passes it in.
     */
    readonly showTime = input(true);
    readonly isUpcomingCatalogue = input(false);
    readonly removeItemConfig = input<{ itemRemovable?: boolean; itemType?: string } | null>(null);

    readonly openDetails = output<IAssetListItem>();
    readonly play = output<IAssetListItem>();
    readonly stop = output<IAssetListItem>();

    protected readonly BadgeVariant = BadgeVariant;
    protected readonly badgePosition = computed(() => this.settings.badgePosition() as BadgePosition ?? BadgePosition.TopRight);

    /** Above the active profile's content rating: artwork is blurred behind a padlock (AVIION-36952). */
    readonly restricted = computed(() => this.gate.isRestricted(this.item()));

    readonly isCircle = computed(() => this.displayFormat() === 'circle');
    readonly isRadio = computed(() => this.displayFormat() === 'radio');

    // Title/subtitle visibility (ported from rc 35985). On the upcoming catalogue the title is
    // governed by the operator setting; elsewhere by the item's own `hideTitle`. The subtitle shows
    // only when the operator enabled it, the item has one, and we're not on the upcoming catalogue.
    readonly showTitle = computed(() => {
        const it = this.item();
        if (!it) return false;
        return this.isUpcomingCatalogue()
            ? this.settings.showTitleOnUpcomingCatalogue()
            : !it.hideTitle;
    });
    readonly showSubtitle = computed(() => {
        const it = this.item();
        return this.settings.showSubtitle() && !!it?.subtitle?.trim() && !this.isUpcomingCatalogue();
    });

    readonly poster = computed(() => {
        const item = this.item();
        // Backend often returns empty strings (not null) for missing posters — coalesce them.
        const pick = (...candidates: (string | null | undefined)[]): string | null => {
            for (const url of candidates) {
                if (url) return url;
            }
            return null;
        };
        switch (this.displayFormat()) {
            case 'portrait': return pick(item.posterPortrait, item.posterLandscape, item.posterSquare, item.imagePath);
            case 'square': return pick(item.posterSquare, item.posterLandscape, item.posterPortrait, item.imagePath);
            case 'circle': return pick(item.posterSquare, item.posterLandscape, item.posterPortrait, item.imagePath);
            default: return pick(item.posterLandscape, item.posterPortrait, item.posterSquare, item.imagePath);
        }
    });

    readonly placeholderIcon = computed(() => this.skinning.getImage('icon_no_image') ?? null);

    readonly showBadge = computed(() => {
        const it = this.item();
        if (it.hideBadge) return false;
        return it.badge != null || it.availabilityStartTime != null || it.availabilityEndTime != null;
    });

    /**
     * Progress 0..100.
     *
     * EPG items (timeStart/timeEnd) win: for a live programme the bar tracks wall-clock, not a
     * bookmark. It has to follow the shared clock — `Date.now()` alone is not reactive and the bar
     * would freeze at its first render.
     *
     * Everything else is VOD-style bookmark progress. The bookmark is SECONDS (the bookmark API's
     * unit, and what `PlaybackProgressStore` records for a freshly played item, overriding the
     * cached value the catalogue was fetched with), so it is divided by the duration in seconds:
     * `durationInSeconds`, or the legacy 25 fps conversion of the `duration` frame count when the
     * backend doesn't send it (see duration.helper). Dividing by raw frames would read ~1/25th.
     */
    readonly progressPercent = computed(() => {
        const item = this.item();
        if (item.timeStart && item.timeEnd) {
            const startMs = new Date(item.timeStart).getTime();
            const endMs = new Date(item.timeEnd).getTime();
            if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return 0;
            return clamp(((this.clock.now() - startMs) / (endMs - startMs)) * 100);
        }
        const played = item.referenceId ? this.playbackProgress.bookmarks()[item.referenceId] : undefined;
        const bookmarkSeconds = played ?? item.bookmark;
        const durationSeconds = assetDurationSeconds(item);
        if (bookmarkSeconds && durationSeconds) {
            return clamp((bookmarkSeconds / durationSeconds) * 100);
        }
        return 0;
    });

    // Start–end, matching the details header and the mosaic/channel-switcher tiles. Only the
    // start used to be rendered, so a programme card never said how long the event runs
    // (AVIION-36669). Events without an end time keep showing just the start.
    readonly airTime = computed(() => {
        const item = this.item();
        if (!item.timeStart) return null;
        const start = formatClockTime(item.timeStart);
        if (!start) return null;
        const end = formatClockTime(item.timeEnd);
        return end ? `${start} - ${end}` : start;
    });

    onClick(): void {
        this.openDetails.emit(this.item());
    }

    onPlay(event: Event): void {
        event.stopPropagation();
        this.play.emit(this.item());
    }

    onStop(event: Event): void {
        event.stopPropagation();
        this.stop.emit(this.item());
    }
}

function clamp(n: number): number {
    return Math.min(100, Math.max(0, n));
}

/** `HH:mm` for an ISO timestamp; null when absent or unparseable. */
function formatClockTime(value: string | number | null | undefined): string | null {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
}
